import { useState } from 'react';
import { X, Twitter, Loader2, Check, AlertCircle, ExternalLink, Copy } from 'lucide-react';

import { TaskProgress, TaskType } from '../types';
import { getTweetTemplate, openTwitterIntent } from '../utils/tweetTemplates';

import { useVerifyTweet } from '@/hooks/useNewYearCampaign';

/**
 * Get the specific requirements for each task type to display to users
 */
function getTaskRequirements(taskType: TaskType): string[] {
  switch (taskType) {
    case TaskType.TWEET_RESULT:
      return [
        '#JunkFunNY2026 hashtag',
        '@junkfun_ mention',
      ];

    case TaskType.REPLY_PROMPT:
      return [
        'Must be a reply to a @junkfun_ tweet',
        'Write your own message - be creative!',
      ];

    case TaskType.RT_USER_CONTENT:
      return [
        'Quote or retweet another participant\'s campaign tweet',
        'Original tweet must have #JunkFunNY2026',
      ];

    // MORNING_CHECKIN task has been removed

    case TaskType.PROGRESS_THREAD:
      return [
        '#JunkFunNY2026 hashtag',
      ];

    case TaskType.MEME_CREATION:
      return [
        '#JunkFunNY2026 hashtag',
        '#JunkFunMeme hashtag',
      ];

    case TaskType.VIRAL_CHALLENGE:
      return [
        '#JunkFunNY2026 hashtag',
        'At least 500 likes OR 100 retweets',
      ];

    case TaskType.THREAD_SERIES:
      return [
        '#JunkFunNY2026 hashtag',
        '@junkfun_ mention',
      ];

    default:
      return [
        '#JunkFunNY2026 hashtag',
      ];
  }
}

interface TweetVerificationModalProps {
  task: TaskProgress;
  onVerify: (tweetUrl: string, originalTweetUrl?: string) => void;
  onClose: () => void;
}

export function TweetVerificationModal({
  task,
  onVerify,
  onClose,
}: TweetVerificationModalProps) {
  const [tweetUrl, setTweetUrl] = useState('');
  const [originalTweetUrl, setOriginalTweetUrl] = useState('');
  const [hasTweeted, setHasTweeted] = useState(false);
  const [copied, setCopied] = useState(false);
  const { mutate: verifyTweet, isPending, error } = useVerifyTweet();
  const [verificationError, setVerificationError] = useState<string | null>(null);

  const tweetTemplate = getTweetTemplate(task.type);
  const isRetweetTask = task.type === TaskType.RT_USER_CONTENT;
  const isManualReview = task.verificationType === 'manual';

  const handleTweetNow = () => {
    if (tweetTemplate) {
      openTwitterIntent(tweetTemplate);
      setHasTweeted(true);
    }
  };

  const handleCopyTemplate = async () => {
    if (tweetTemplate) {
      await navigator.clipboard.writeText(tweetTemplate);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleVerify = () => {
    if (!tweetUrl.trim()) {
      setVerificationError('Please enter your tweet URL');
      return;
    }

    // Basic URL validation
    if (!tweetUrl.match(/(?:twitter\.com|x\.com)\/\w+\/status\/\d+/)) {
      setVerificationError('Please enter a valid Twitter/X post URL');
      return;
    }

    // For retweet task, require the original tweet URL
    if (isRetweetTask) {
      if (!originalTweetUrl.trim()) {
        setVerificationError('Please enter the original tweet URL you are sharing');
        return;
      }
      if (!originalTweetUrl.match(/(?:twitter\.com|x\.com)\/\w+\/status\/\d+/)) {
        setVerificationError('Please enter a valid URL for the original tweet');
        return;
      }
    }

    setVerificationError(null);

    verifyTweet(
      { tweetUrl },
      {
        onSuccess: () => {
          onVerify(tweetUrl, isRetweetTask ? originalTweetUrl : undefined);
        },
        onError: (err: Error) => {
          setVerificationError(err.message || 'Verification failed');
        },
      }
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      style={{ fontFamily: 'Outfit, sans-serif' }}
    >
      <div className="bg-gray-900 rounded-2xl border border-gray-700 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Twitter className="w-5 h-5 text-cyan-400" />
            Verify Your Tweet
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Task info */}
          <div className="bg-gray-800/50 rounded-lg p-3 mb-4">
            <p className="text-white font-medium">{task.name}</p>
            <p className="text-gray-400 text-sm mt-1">{task.description}</p>
            <p className="text-cyan-400 font-semibold mt-2">+{task.points} points</p>
          </div>

          {/* Manual review notice */}
          {isManualReview && (
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-amber-400 text-sm">
                ⏳ This task requires admin review. After submitting, your entry will be reviewed and points will be awarded once approved.
              </p>
            </div>
          )}

          {/* Tweet Template Preview */}
          {tweetTemplate && !hasTweeted && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-gray-400 text-sm">Tweet Template:</p>
                <button
                  onClick={handleCopyTemplate}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
                >
                  <Copy className="w-3 h-3" />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                <p className="text-gray-200 text-sm whitespace-pre-wrap">{tweetTemplate}</p>
              </div>
              <p className="text-gray-500 text-xs mt-2">
                Feel free to customize this template before posting!
              </p>
            </div>
          )}

          {/* Step indicator */}
          {tweetTemplate && (
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${hasTweeted ? 'bg-green-500 text-white' : 'bg-cyan-500 text-white'}`}>
                {hasTweeted ? <Check className="w-4 h-4" /> : '1'}
              </div>
              <div className="flex-1 h-0.5 bg-gray-700">
                <div className={`h-full transition-all ${hasTweeted ? 'bg-cyan-500 w-full' : 'w-0'}`} />
              </div>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${hasTweeted ? 'bg-cyan-500 text-white' : 'bg-gray-700 text-gray-400'}`}>
                2
              </div>
            </div>
          )}

          {/* Tweet Now Button (Step 1) */}
          {tweetTemplate && !hasTweeted && (
            <button
              onClick={handleTweetNow}
              className="w-full px-4 py-3 rounded-lg bg-[#1DA1F2] text-white font-semibold hover:bg-[#1a8cd8] transition-colors flex items-center justify-center gap-2 mb-4"
            >
              <ExternalLink className="w-4 h-4" />
              Tweet Now
            </button>
          )}

          {/* Requirements reminder */}
          {hasTweeted && (
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-400 text-sm flex items-center gap-2">
                <Check className="w-4 h-4" />
                Great! Now paste your tweet URL below to verify.
              </p>
            </div>
          )}

          {/* Requirements - Task-specific */}
          <div className="mb-4">
            <p className="text-gray-400 text-sm mb-2">Requirements:</p>
            <ul className="space-y-1 text-sm">
              {getTaskRequirements(task.type).map((req, index) => (
                <li key={index} className="flex items-center gap-2 text-gray-300">
                  <Check className="w-4 h-4 text-green-400" />
                  {req}
                </li>
              ))}
            </ul>
          </div>

          {/* Original tweet URL input (for retweet task) */}
          {isRetweetTask && (
            <div className="mb-4">
              <label className="block text-gray-400 text-sm mb-2">
                Original tweet URL (the tweet you are sharing)
              </label>
              <input
                type="url"
                value={originalTweetUrl}
                onChange={(e) => setOriginalTweetUrl(e.target.value)}
                placeholder="https://x.com/original_author/status/..."
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
              />
              <p className="text-gray-500 text-xs mt-1">
                Must be a tweet from another campaign participant with #JunkFunNY2026
              </p>
            </div>
          )}

          {/* Your tweet URL input */}
          <div className="mb-4">
            <label className="block text-gray-400 text-sm mb-2">
              {isRetweetTask ? 'Your quote tweet or retweet URL' : 'Paste your tweet URL'}
            </label>
            <input
              type="url"
              value={tweetUrl}
              onChange={(e) => setTweetUrl(e.target.value)}
              placeholder="https://x.com/username/status/123..."
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
            />
          </div>

          {/* Error message */}
          {(verificationError || error) && (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">
                {verificationError || (error as Error)?.message || 'Verification failed'}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-lg bg-gray-800 text-gray-300 font-semibold hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleVerify}
              disabled={isPending || !tweetUrl.trim()}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                isManualReview
                  ? 'bg-amber-500 text-white hover:bg-amber-400'
                  : 'bg-cyan-500 text-white hover:bg-cyan-400'
              }`}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isManualReview ? 'Submitting...' : 'Verifying...'}
                </>
              ) : isManualReview ? (
                'Submit for Review'
              ) : (
                'Verify & Complete'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
